import { useState } from 'react';

import type { ActionIntent, MultiChoiceOption } from '../../services/agentDraftApi';

interface MultiChoiceMessageProps {
  question: string;
  mode: 'single' | 'multi';
  options: MultiChoiceOption[];
  submitLabel?: string;
  onConfirm: (params: { displayMessage: string; intent: ActionIntent }) => void;
}

export function MultiChoiceMessage({
  question,
  mode,
  options,
  submitLabel,
  onConfirm,
}: MultiChoiceMessageProps) {
  const [selected, setSelected] = useState<string[]>([]);

  const fire = (values: string[]) => {
    const labels = options
      .filter((o) => values.includes(o.value))
      .map((o) => o.label)
      .join(', ');
    onConfirm({
      displayMessage: `${question}: ${labels}`,
      intent: {
        type: 'submit_multi_choice',
        payload: { question, selected: values },
      },
    });
  };

  const toggleSingle = (value: string) => {
    // Single mode: clique já confirma — não acumula estado nem precisa de submit
    fire([value]);
  };

  const toggleMulti = (value: string) => {
    setSelected((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    );
  };

  const handleSubmit = () => {
    if (!selected.length) return;
    fire(selected);
  };

  return (
    <div
      data-testid="multi-choice-message"
      style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 8 }}
    >
      <div
        style={{
          fontSize: 13,
          fontWeight: 600,
          color: '#1f2937',
          marginBottom: 2,
        }}
      >
        {question}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {options.map((option) => {
          const isSelected =
            mode === 'multi' ? selected.includes(option.value) : false;
          return (
            <button
              key={option.value}
              onClick={() =>
                mode === 'single'
                  ? toggleSingle(option.value)
                  : toggleMulti(option.value)
              }
              data-testid={`multi-choice-option-${option.value}`}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 10,
                padding: '10px 12px',
                borderRadius: 8,
                border: `2px solid ${isSelected ? '#8b5cf6' : '#e5e7eb'}`,
                background: isSelected ? '#f5f3ff' : '#fff',
                cursor: 'pointer',
                textAlign: 'left',
                fontSize: 13,
                color: '#374151',
                width: '100%',
              }}
            >
              <div
                style={{
                  flexShrink: 0,
                  marginTop: 1,
                  width: 16,
                  height: 16,
                  borderRadius: mode === 'multi' ? 4 : '50%',
                  border: `2px solid ${isSelected ? '#8b5cf6' : '#cbd5e1'}`,
                  background: isSelected ? '#8b5cf6' : '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {isSelected && (
                  <span
                    style={{
                      color: '#fff',
                      fontSize: 11,
                      fontWeight: 700,
                      lineHeight: 1,
                    }}
                  >
                    ✓
                  </span>
                )}
              </div>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span style={{ fontWeight: 600 }}>{option.label}</span>
                {option.description && (
                  <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 400 }}>
                    {option.description}
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>
      {mode === 'multi' && (
        <button
          onClick={handleSubmit}
          disabled={!selected.length}
          data-testid="multi-choice-submit"
          style={{
            alignSelf: 'flex-start',
            padding: '8px 16px',
            borderRadius: 8,
            border: 'none',
            background: selected.length ? '#8b5cf6' : '#e5e7eb',
            color: selected.length ? '#fff' : '#9ca3af',
            fontWeight: 600,
            fontSize: 14,
            cursor: selected.length ? 'pointer' : 'not-allowed',
          }}
        >
          {submitLabel ?? 'Confirmar seleção'}
        </button>
      )}
    </div>
  );
}
